// src/hooks/useSoundsData.js
import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuth from './useAuth';
import api from '../services/api';
import {
  useDateTimeFilter,
  toInputFormat,
  toBackendFormat,
  getDefaultDateFrom,
  getDefaultDateTo,
} from './useDateTimeFilter';
import { useFilterState } from './useFilterState';

export const useSoundsData = () => {
  const { user: currentUser } = useAuth();
  const [sounds, setSounds] = useState([]);
  const [selections, setSelections] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, per_page: 100 });
  const [projects, setProjects] = useState([]);

  const location = useLocation();
  const navigate = useNavigate();

  // === Глобальные состояния ===
  const [activeSoundId, setActiveSoundId] = useState(null);
  const [activeTextSoundId, setActiveTextSoundId] = useState(null);

  // === Фильтры дат ===
  const dateTimeFilter = useDateTimeFilter('', '');
  const {
    filters: dateFilters,
    tempValues: tempDateValues,
    handleChange: handleDateChange,
    handleBlur: handleDateBlur,
  } = dateTimeFilter;

  // === Текстовые фильтры (требуют подтверждения через blur/enter) ===
  const textFilter = useFilterState({
    search_phone: '',
    search_operator: '',
    duration: '',
    line: '',
    text_search: '',
    sound_id: ''
  });

  const {
    filters: textFilters,
    tempValues: tempTextValues,
    handleTextChange,
    handleTextBlur,
    handleTextKeyDown,
  } = textFilter;

  // === Основные фильтры (применяются сразу) ===
  const [filters, setFilters] = useState({
    project: '',
    has_assessment: '', // '', 'true', 'false'
    has_assessment_in_analytics: '', // '', 'true', 'false'
    duration_op: '',
    stopside: '',
    side: '',
    text_search_scope: ''
  });

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const handleHasAssessmentChange = useCallback((value) => {
    setFilters((prev) => ({ ...prev, has_assessment: value }));
    setCurrentPage(1);
  }, []);

  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 100;
  const [isFetching, setIsFetching] = useState(false);

  // === Защита от повторных запросов при инициализации ===
  const isInitialized = useRef(false);
  const isInitializing = useRef(false);

  // === Инициализация из URL ===
  useEffect(() => {
    if (!isInitialized.current) {
      const searchParams = new URLSearchParams(location.search);
      const project = searchParams.get('project') || '';
      const phone = searchParams.get('search_phone') || '';
      const operator = searchParams.get('search_operator') || '';
      const duration = searchParams.get('duration') || '';
      const line = searchParams.get('line') || '';
      const text_search = searchParams.get('text_search') || '';
      const sound_id = searchParams.get('sound_id') || '';
      const page = parseInt(searchParams.get('page')) || 1;
      const date_from = searchParams.get('date_from') || '';
      const date_to = searchParams.get('date_to') || '';
      const has_assessment = searchParams.get('has_assessment') || '';
      const has_assessment_in_analytics = searchParams.get('has_assessment_in_analytics') || '';
      const duration_op = searchParams.get('duration_op') || '';
      const stopside = searchParams.get('stopside') || '';
      const side = searchParams.get('side') || '';
      const text_search_scope = searchParams.get('text_search_scope') || '';

      setFilters({
        project,
        has_assessment,
        has_assessment_in_analytics,
        duration_op,
        stopside,
        side,
        text_search_scope
      });
      textFilter.setFilters({
        search_phone: phone,
        search_operator: operator,
        duration: duration,
        line: line,
        text_search: text_search,
        sound_id: sound_id
      });
      setCurrentPage(page);

      if (date_from || date_to) {
        const inputFrom = date_from ? toInputFormat(date_from) : '';
        const inputTo = date_to ? toInputFormat(date_to) : '';
        isInitializing.current = true;
        handleDateBlur({ target: { name: 'date_filter_from', value: inputFrom } });
        handleDateBlur({ target: { name: 'date_filter_to', value: inputTo } });
        isInitializing.current = false;
      }

      isInitialized.current = true;
    }
  }, [location.search, handleDateBlur, textFilter]);

  // === Загрузка данных ===
  const fetchData = async () => {
    setIsFetching(true);
    try {
      const dateFromToSend = dateFilters.date_filter_from ? toBackendFormat(dateFilters.date_filter_from) : '';
      const dateToToSend = dateFilters.date_filter_to ? toBackendFormat(dateFilters.date_filter_to) : '';


      const params = new URLSearchParams({
        page: currentPage,
        project: filters.project,
        search_phone: textFilters.search_phone,
        search_operator: textFilters.search_operator,
        date_from: dateFromToSend,
        date_to: dateToToSend,
        duration: textFilters.duration,
        duration_op: filters.duration_op,
        stopside: filters.stopside,
        line: textFilters.line,
        side: filters.side,
        text_search: textFilters.text_search,
        text_search_scope: filters.text_search_scope,
        sound_id: textFilters.sound_id,
        has_assessment: filters.has_assessment,
        has_assessment_in_analytics: filters.has_assessment_in_analytics
      });

      // Удаляем пустые параметры и значения undefined/null
      for (const [key, value] of params.entries()) {
        if (value === '' || value === 'undefined' || value === 'null') {
          params.delete(key);
        }
      }

      const res = await api.get('/sounds', { params });
      const data = res.data;

      setSounds(data.sounds || []);
      setTotalRecords(data.total || 0);
      setPagination({
        page: data.page || 1,
        pages: data.pages || 1,
        per_page: data.per_page || 100,
      });

      if (Array.isArray(data.projects)) {
        setProjects(data.projects.map(p => [p.id, p.name]));
      }

      const serverFrom = data.date_filter_from || getDefaultDateFrom();
      const serverTo = data.date_filter_to || getDefaultDateTo();

      const inputFrom = toInputFormat(serverFrom);
      const inputTo = toInputFormat(serverTo);

      // Проверяем только если значения действительно изменились
      if (dateFilters.date_filter_from !== inputFrom) {
        handleDateBlur({ target: { name: 'date_filter_from', value: inputFrom } });
      }
      if (dateFilters.date_filter_to !== inputTo) {
        handleDateBlur({ target: { name: 'date_filter_to', value: inputTo } });
      }

      // Создаем URL параметры из отдельных состояний фильтров
      const newParams = new URLSearchParams();

      if (currentPage !== 1) newParams.append('page', currentPage);
      if (filters.project) newParams.append('project', filters.project);
      if (textFilters.search_phone) newParams.append('search_phone', textFilters.search_phone);
      if (textFilters.search_operator) newParams.append('search_operator', textFilters.search_operator);
      if (serverFrom) newParams.append('date_from', serverFrom);
      if (serverTo) newParams.append('date_to', serverTo);
      if (filters.has_assessment !== '') newParams.append('has_assessment', filters.has_assessment);
      if (filters.has_assessment_in_analytics !== '') newParams.append('has_assessment_in_analytics', filters.has_assessment_in_analytics);
      if (textFilters.duration !== '') newParams.append('duration', textFilters.duration);
      if (filters.duration_op !== '') newParams.append('duration_op', filters.duration_op);
      if (filters.stopside !== '') newParams.append('stopside', filters.stopside);
      if (textFilters.line !== '') newParams.append('line', textFilters.line);
      if (filters.side !== '') newParams.append('side', filters.side);
      if (textFilters.text_search !== '') newParams.append('text_search', textFilters.text_search);
      if (filters.text_search_scope !== '') newParams.append('text_search_scope', filters.text_search_scope);
      if (textFilters.sound_id !== '') newParams.append('sound_id', textFilters.sound_id);

      // Удаляем пустые параметры
      for (const [key, value] of newParams.entries()) {
        if (value === '' || value === 'undefined' || value === 'null') {
          newParams.delete(key);
        }
      }

      navigate({ search: newParams.toString() }, { replace: true });
    } catch (err) {
      console.error('Ошибка загрузки данных:', err);
      setSounds([]);
      setTotalRecords(0);
    } finally {
      setIsFetching(false);
    }
  };

  // === Загрузка выборок ===
  const fetchSelections = async () => {
    try {
      const res = await api.get('/quality/assessment-selection');
      setSelections(res.data.data || []);
    } catch (err) {
      console.error('Ошибка загрузки выборок:', err);
      setSelections([]);
    }
  };

  // === Effect для загрузки данных с защитой от повторных запросов ===
  // Используем JSON.stringify для сравнения значений объектов, а не ссылок
  const filtersString = JSON.stringify(filters);
  const dateFiltersString = JSON.stringify(dateFilters);
  const textFiltersString = JSON.stringify(textFilters);

  useEffect(() => {
    // Пропускаем запрос во время инициализации
    if (isInitializing.current) {
      return;
    }

    // Дебаунс для предотвращения избыточных запросов
    const timeoutId = setTimeout(() => {
      fetchData();
      fetchSelections();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [currentPage, filtersString, dateFiltersString, textFiltersString]);

  // === Сброс фильтров ===
  const resetFilters = () => {
    dateTimeFilter.reset();
    textFilter.reset();
    // Сбрасываем все фильтры
    setFilters({
      project: '',
      has_assessment: '',
      has_assessment_in_analytics: '',
      duration_op: '',
      stopside: '',
      side: '',
      text_search_scope: ''
    });
    textFilter.reset();
    setCurrentPage(1);
    navigate({ search: '' }, { replace: true });
  };

  // === Быстрые фильтры ===
  const handleToday = () => {
    const today = new Date().toISOString().slice(0, 16);
    handleDateBlur({ target: { name: 'date_filter_from', value: today } });
    handleDateBlur({ target: { name: 'date_filter_to', value: '' } });
  };

  const handleYesterday = () => {
    const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 16);
    const endOfDay = new Date().toISOString().slice(0, 11) + '23:59';
    handleDateBlur({ target: { name: 'date_filter_from', value: yesterday } });
    handleDateBlur({ target: { name: 'date_filter_to', value: endOfDay } });
  };

  // === Скачать запись ===
  const handleDownloadSound = async (soundId) => {
    try {
      const response = await api.get(`/api/sound/audio/${soundId}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'audio/mpeg' });
      const url = window.URL.createObjectURL(blob);
      // Force HTTPS for the blob URL
      const sound = sounds.find(s => s.id === soundId);
      const filename = sound?.name_sound || `запись_${soundId}`;
      const cleanFilename = filename.trim().replace(/[/\\?%*:|"<>]/g, '_');

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${cleanFilename}.mp3`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Ошибка при скачивании аудио:', error);
      window.showToast('Не удалось скачать запись');
    }
  };

  // === Экспорт ===
  const handleExport = async () => {
    try {
      const dateFromToSend = dateFilters.date_filter_from ? toBackendFormat(dateFilters.date_filter_from) : '';
      const dateToToSend = dateFilters.date_filter_to ? toBackendFormat(dateFilters.date_filter_to) : '';

      const params = new URLSearchParams({
        project: filters.project,
        search_phone: textFilters.search_phone,
        search_operator: textFilters.search_operator,
        date_from: dateFromToSend,
        date_to: dateToToSend,
      });

      const url = `/sounds/download?${params.toString()}`;
      const response = await api.get(url, { responseType: 'blob' });

      let filename = 'записи.zip';
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^\"]+)"?/);
        if (match && match[1]) {
          filename = match[1];
        }
      }
      filename = filename.replace(/[/\\?%*:|"<>]/g, '_');

      const blob = new Blob([response.data], { type: 'application/zip' });
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      if (error.response?.status === 400) {
        window.showToast('Нет записей для скачивания');
      } else if (error.response?.status === 403) {
        window.showToast('Нет доступа к проектам');
      } else {
        window.showToast('Произошла ошибка при экспорте');
      }
    }
  };

  const isInSelection = (assessmentId) => {
    return selections.some(s => s.quality_assessment_id === assessmentId);
  };

  const handleToggleSelection = async (sound) => {
    if (!sound.assessment_id) return;

    const projectId = sound.project_id;
    const assessmentId = sound.assessment_id;

    // Проверяем, есть ли уже запись в selection
    const existingSelection = selections.find(s => s.quality_assessment_id === assessmentId);

    if (existingSelection) {
      // Удаляем из анализа
      try {
        await api.delete(`/quality/assessment-selection/${existingSelection.id}`);
        // Обновляем локальный список после удаления
        setSelections(prev => prev.filter(s => s.id !== existingSelection.id));
        // window.showToast('Звонок удалён из анализа');
      } catch (error) {
        console.error('Ошибка при удалении из анализа:', error);
        window.showToast('Не удалось удалить звонок из анализа');
      }
    } else {
      // Добавляем в анализ
      try {
        const response = await api.post('/quality/assessment-selection', {
          quality_assessment_id: assessmentId,
          project_id: projectId,
          added_by_id: currentUser.id
        });
        // Добавляем новую запись в локальный список
        setSelections(prev => [response.data, ...prev]);
        window.showToast('Звонок добавлен в анализ');
      } catch (error) {
        console.error('Ошибка при добавлении в анализ:', error);
        window.showToast('Не удалось добавить звонок в анализ');
      }
    }
  };

  const totalPages = Math.ceil(totalRecords / perPage);
  const start = (currentPage - 1) * perPage + 1;
  const end = Math.min(currentPage * perPage, totalRecords);
  const isSuperuser = currentUser?.superuser || false;

  return {
    sounds,
    projects,
    totalRecords,
    pagination,
    isSuperuser,
    filters: { ...textFilters, ...filters },
    dateFilters,
    tempTextValues,
    tempDateValues,
    handleTextChange,
    handleTextBlur,
    handleTextKeyDown,
    handleFilterChange,
    handleDateChange,
    handleDateBlur,
    handleToday,
    handleYesterday,
    resetFilters,
    currentPage,
    setCurrentPage,
    start,
    end,
    activeSoundId,
    setActiveSoundId,
    activeTextSoundId,
    setActiveTextSoundId,
    handleDownloadSound,
    handleExport,
    isFetching,
    perPage,
    isInSelection,
    handleToggleSelection,
    handleHasAssessmentChange,
  };
};

export default useSoundsData;
